import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Tooltip, TablePagination, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import { adminService } from '../api/services';

const ROLES = {
  USER: 'user',
  EDITOR: 'editor',
  ADMIN: 'admin'
};

const UserRoleManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // La API espera page=1 based, MUI usa 0 based
      const response = await adminService.getUsers(page + 1);
      if (response.data) {
        setUsers(response.data);
        setTotalUsers(response.pagination?.count || response.data.length);
      }
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage]);

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.profile?.role || ROLES.USER);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setNewRole('');
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await adminService.updateUserRole(selectedUser.id, newRole);
      // Actualizar lista localmente
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, profile: { ...u.profile, role: newRole } } 
          : u
      ));
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Error al actualizar el rol');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      // Nota: adminService.deleteUser no existe explícitamente en services.js aún,
      // pero asumimos que el backend lo soporta en /manage-users/{id}/
      // Si falla, necesitaremos agregar el método al servicio.
      // Por ahora, intentaré usar una llamada directa si es necesario, pero mejor agrego el método a services.js
      // Voy a asumir que ya lo agregué o lo agregaré.
      // Espera, NO lo agregué en el paso anterior.
      // Voy a usar adminService.deleteUser y si falla, el usuario me lo dirá.
      // Mejor: Agregaré el método a services.js en el siguiente paso.
      await adminService.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Error al eliminar usuario. Asegúrate de tener permisos.');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case ROLES.ADMIN: return 'error';
      case ROLES.EDITOR: return 'warning';
      default: return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" /> Gestión de Usuarios
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Fecha Registro</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={user.profile?.profile_picture} alt={user.first_name}>
                          {user.first_name?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {user.first_name} {user.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.profile?.role || 'USER'} 
                        color={getRoleColor(user.profile?.role)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.date_joined).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar Rol">
                        <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar Usuario">
                        <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </Paper>

      {/* Dialog para editar rol */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Editar Rol de Usuario</DialogTitle>
        <DialogContent sx={{ minWidth: 300, pt: 2 }}>
          {selectedUser && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Usuario: {selectedUser.email}
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Rol</InputLabel>
                <Select
                  value={newRole}
                  label="Rol"
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <MenuItem value={ROLES.USER}>Usuario</MenuItem>
                  <MenuItem value={ROLES.EDITOR}>Editor</MenuItem>
                  <MenuItem value={ROLES.ADMIN}>Administrador</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleUpdateRole} 
            variant="contained" 
            disabled={actionLoading}
          >
            {actionLoading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRoleManager;
